import axios from 'axios';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Returns the API base URL based on the current environment
 * @returns {string} API base URL
 */
export const toggleEnvironment = () => API_BASE_URL;

/**
 * Determine if relative URLs should be used
 * @returns {boolean} true if relative URLs should be used, false otherwise
 */
export const shouldUseRelativeUrls = () => false;

/**
 * Get the API prefix
 * @returns {string} API prefix
 */
export const getApiPrefix = () => '/api/v1/stem@home';

// Get auth headers from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercept requests to add auth headers
apiClient.interceptors.request.use(
  (config) => {
    // Add auth headers to every request
    const headers = getAuthHeaders();
    config.headers = {
      ...config.headers,
      ...headers
    };
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercept responses to handle common errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    
    // Handle auth errors
    if (error.response && error.response.status === 401) {
      // Clear token if unauthorized
      localStorage.removeItem('token');
      
      // Redirect to login if needed
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API client methods
const api = {
  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Query parameters
   * @returns {Promise} API response
   */
  get: async (endpoint, params = {}) => {
    try {
      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request data
   * @returns {Promise} API response
   */
  post: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request data
   * @returns {Promise} API response
   */
  put: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise} API response
   */
  delete: async (endpoint) => {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  }
};

/**
 * Handle API request errors
 * @param {Error} error - Axios error
 */
const handleRequestError = (error) => {
  // Add user-friendly messages based on error status
  if (error.response) {
    // Server responded with an error
    if (error.response.status === 403) {
      error.friendlyMessage = 'You do not have permission to perform this action.';
    } else if (error.response.status === 404) {
      error.friendlyMessage = 'The requested resource was not found.';
    } else if (error.response.status === 500) {
      error.friendlyMessage = 'Server error. Please try again later.';
    } else {
      error.friendlyMessage = error.response.data?.message || 'An error occurred.';
    }
  } else if (error.request) {
    // Request made but no response
    error.friendlyMessage = 'Unable to connect to the server. Please check your internet connection.';
  } else {
    // Something else caused the error
    error.friendlyMessage = 'An unexpected error occurred. Please try again.';
  }
};

export default api; 